import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../lib/auth";
import { CHECKLIST_TYPE_LABEL } from "../lib/constants";

export default function ChecklistsList({ mineOnly = false, types = null }) {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [boats, setBoats] = useState([]);
  const [users, setUsers] = useState([]);
  // Service can toggle between "alle" and "tildelt meg" to focus on own tasks.
  const [showMineOnly, setShowMineOnly] = useState(mineOnly);

  useEffect(() => {
    const params = {};
    if (showMineOnly) params.assigned_to_me = true;
    Promise.all([
      api.get("/checklists", { params }),
      api.get("/boats"),
      // Used to resolve "Tatt av X (rolle)" badges. /users/directory is open to all staff roles.
      api.get("/users/directory").catch(() => ({ data: [] })),
    ]).then(([cl, b, u]) => {
      let items = cl.data;
      if (types) items = items.filter((c) => types.includes(c.type));
      setList(items);
      setBoats(b.data);
      setUsers(u.data);
    });
  }, [showMineOnly, types]);

  const boatName = (id) => boats.find((b) => b.id === id)?.name || "—";
  const canToggleMine = user?.role === "service" || user?.role === "admin";

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.32em] text-[#A57F39]">Oppgaver</div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0C1824] mt-1">
            {showMineOnly ? "Mine sjekklister" : "Sjekklister"}
          </h1>
        </div>
        {canToggleMine && (
          <div className="inline-flex rounded-lg border border-[#BD984D]/30 bg-white p-1"
               data-testid="cl-list-scope">
            <button
              type="button"
              onClick={() => setShowMineOnly(false)}
              data-testid="cl-list-scope-all"
              className={`px-3 py-1.5 text-xs rounded-md font-semibold transition-colors ${
                !showMineOnly ? "bg-[#0C1824] text-white" : "text-[#2B3F4D] hover:bg-slate-50"
              }`}
            >
              Alle
            </button>
            <button
              type="button"
              onClick={() => setShowMineOnly(true)}
              data-testid="cl-list-scope-mine"
              className={`px-3 py-1.5 text-xs rounded-md font-semibold transition-colors ${
                showMineOnly ? "bg-[#0C1824] text-white" : "text-[#2B3F4D] hover:bg-slate-50"
              }`}
            >
              Tildelt meg
            </button>
          </div>
        )}
      </div>

      <div className="space-y-3">
        {list.length === 0 && <Card className="p-6 text-sm text-[#2B3F4D]">Ingen sjekklister.</Card>}
        {list.map((cl) => {
          const holderId = cl.assigned_to;
          const holder = holderId ? users.find((u) => u.id === holderId) : null;
          const isMine = holderId && holderId === user.id;
          const isOpen = !holderId && cl.status !== "completed";
          return (
            <Card key={cl.id} className="p-4" data-testid={`cl-list-${cl.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="text-xs uppercase tracking-[0.18em] text-[#A57F39]">
                    {CHECKLIST_TYPE_LABEL[cl.type]} · {boatName(cl.boat_id)}
                  </div>
                  <div className="font-semibold text-[#0C1824] mt-1">
                    {cl.status === "completed" ? "Fullført" : "Venter på utførelse"}
                  </div>
                  <div className="text-xs text-[#2B3F4D] mt-1 flex flex-wrap items-center gap-2">
                    <span>
                      {cl.completed_at
                        ? `Fullført ${new Date(cl.completed_at).toLocaleString("nb-NO")} av ${cl.performed_by_name}`
                        : `Opprettet ${new Date(cl.created_at).toLocaleString("nb-NO")}`}
                    </span>
                    {isOpen && (
                      <Badge
                        className="bg-emerald-100 text-emerald-700 border-0"
                        data-testid={`cl-list-${cl.id}-open`}
                      >
                        Ledig – ta den
                      </Badge>
                    )}
                    {isMine && (
                      <Badge
                        className="bg-[#1DA3B3]/15 text-[#1DA3B3] border-0"
                        data-testid={`cl-list-${cl.id}-mine`}
                      >
                        Tatt av meg
                      </Badge>
                    )}
                    {!isMine && holder && cl.status !== "completed" && (
                      <Badge
                        className="bg-[#BD984D]/15 text-[#A57F39] border-0"
                        data-testid={`cl-list-${cl.id}-taken`}
                      >
                        Tatt av {holder.name} ({holder.role})
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Badge
                    className={
                      cl.status === "completed"
                        ? "bg-[#1DA3B3]/15 text-[#1DA3B3]"
                        : "bg-[#BD984D]/15 text-[#A57F39]"
                    }
                  >
                    {cl.status === "completed" ? "Fullført" : "Venter"}
                  </Badge>
                  <Link to={`/checklist/${cl.id}`}>
                    <Button className="bg-[#BD984D] hover:bg-[#A57F39] text-white">
                      {cl.status === "completed" ? "Se" : isMine ? "Fullfør" : isOpen ? "Ta + åpne" : "Åpne"}
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
